import { B400, N500, N700 } from '@atlaskit/theme/colors';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  ICON_STATES,
  SecondaryProductDisplayPropsType,
  SecondaryProductSelectionContainerPropsType
} from '../../model';
import { ProductIcon } from '../icon';
import { messagesByProductKey } from '../messages';
import { SecondaryProductSelectButton } from '../secondary-product-select-button';
import { CharlieDisplayFontStack } from '../styled';

export function SecondaryProductDisplayView({
  productKey,
  selected = false,
  onSelect = () => undefined,
  btnId
}: SecondaryProductDisplayPropsType) {
  return (
    <SecondaryProductSelectionContainer selected={selected}>
      <SecondaryProductSelectionColumnLeft>
        <SecondaryProductContainer>
          <ProductIcon
            productKey={productKey}
            iconState={ICON_STATES.SECONDARY_PRODUCT}
          />
          <SecondaryProductTextContentContainer>
            <SecondaryProductHeading>
              <FormattedMessage {...messagesByProductKey[productKey].name} />
            </SecondaryProductHeading>
            <SecondaryProductSubheading productKey={productKey}>
              <FormattedMessage
                {...messagesByProductKey[productKey].subHeading}
              />
            </SecondaryProductSubheading>
          </SecondaryProductTextContentContainer>
        </SecondaryProductContainer>
        <SecondaryProductDescription>
          <FormattedMessage {...messagesByProductKey[productKey].description} />
        </SecondaryProductDescription>
      </SecondaryProductSelectionColumnLeft>
      <SecondaryProductSelectionColumnRight>
        <SecondaryProductSelectButton
          isSelected={selected}
          handleClick={onSelect}
          btnId={btnId}
        />
      </SecondaryProductSelectionColumnRight>
    </SecondaryProductSelectionContainer>
  );
}

const SecondaryProductSelectionContainer = styled.div<SecondaryProductSelectionContainerPropsType>`
  display: flex;
  flex-direction: row;
  max-width: 100%;
  width: 100%;
  min-height: 130px;
  margin: 0 0 22px 0;
  padding: 20px 16px;
  border: ${({ selected }: SecondaryProductSelectionContainerPropsType) =>
    selected ? `1px solid ${B400} !important` : '1px solid #cdd1d8'};
  box-sizing: border-box;
  background-color: ${({
    selected
  }: SecondaryProductSelectionContainerPropsType) =>
    selected ? '#F4F8FF !important' : ''};

  @media (max-width: 1160px) {
    flex-direction: column;
    align-items: flex-end;
  }

  @media (max-width: 400px) {
    padding: 18px 12px;
  }
`;

const SecondaryProductSelectionColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const SecondaryProductSelectionColumnLeft = styled(
  SecondaryProductSelectionColumn
)`
  width: 75%;
  justify-content: space-between;

  @media (max-width: 1160px) {
    width: 100%;
  }
`;

const SecondaryProductSelectionColumnRight = styled(
  SecondaryProductSelectionColumn
)`
  width: 25%;

  @media (max-width: 1160px) {
    width: 142px;
    margin-top: 14px;
  }

  @media (max-width: 778px) {
    width: 110px;
  }
`;

const SecondaryProductContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  margin-left: -5px;
  margin-bottom: 10px;
`;

const SecondaryProductTextContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  line-height: 1.2;
`;

const SecondaryProductHeading = styled.h2`
  font-family: ${CharlieDisplayFontStack};
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: ${N700};
  letter-spacing: 0px;
  line-height: 28px;

  @media (max-width: 400px) {
    font-size: 18px;
  }
`;

const SecondaryProductSubheading = styled.h3<{ productKey: string }>`
  font-family: ${CharlieDisplayFontStack};
  font-weight: 300;
  margin: 0;
  font-size: 14px;
  color: ${N500};
  letter-spacing: 0.3px;

  @media (max-width: 400px) {
    font-size: ${({ productKey }: { productKey: string }) =>
      productKey === 'jira-core.ondemand' ? '11px' : '12px'};
  }
`;

const SecondaryProductDescription = styled.p`
  font-size: 14px;
  color: ${N500};
  font-weight: 400;
  letter-spacing: 0.21px;
  line-height: 18px;
  margin: 0;
  font-family: ${CharlieDisplayFontStack};
`;
